// @noflow

/* eslint-disable i18next/no-literal-string */
import {
  Switch as MUISwitch,
  Theme as MUITheme,
  SwitchProps,
  SxProps
} from '@mui/material'
import React, { useMemo } from 'react'
import { valueof } from 'react-joyride'

import BRAND_COLOURS, { BrandColours } from '@/constants/BrandColours'

import { ICON_COLOURS, IconColours } from '../Icon/Icon'

type ThemeVariant = 'default' | 'success' | 'danger'
type SizeVariant = 'small' | 'medium' | 'large'

type ExtendedInputProps = {
  'data-theme'?: ThemeVariant
  'data-size'?: SizeVariant
} & SwitchProps['inputProps']

type Props = {
  theme?: ThemeVariant
  variant?: SizeVariant
  inputProps?: ExtendedInputProps
} & SwitchProps

type Theme = {
  unselected: {
    backgroundColor: valueof<BrandColours> | valueof<IconColours>
    borderColor: valueof<BrandColours> | valueof<IconColours>
    circleColor: valueof<BrandColours> | valueof<IconColours>
  }
  selected: {
    backgroundColor: valueof<BrandColours> | valueof<IconColours>
    borderColor: valueof<BrandColours> | valueof<IconColours>
    circleColor: valueof<BrandColours> | valueof<IconColours>
  }
}

const DEFAULT_THEME: Theme = {
  unselected: {
    backgroundColor: BRAND_COLOURS.brandBlue100,
    borderColor: BRAND_COLOURS.brandBlue500,
    circleColor: BRAND_COLOURS.brandBlue500
  },
  selected: {
    backgroundColor: BRAND_COLOURS.brandBlue500,
    borderColor: BRAND_COLOURS.brandBlue500,
    circleColor: BRAND_COLOURS.brandWhite
  }
}

const SUCCESS_THEME: Theme = {
  unselected: {
    backgroundColor: ICON_COLOURS.successGreen100,
    borderColor: ICON_COLOURS.successGreen500,
    circleColor: ICON_COLOURS.successGreen500
  },
  selected: {
    backgroundColor: ICON_COLOURS.successGreen500,
    borderColor: ICON_COLOURS.successGreen500,
    circleColor: BRAND_COLOURS.brandWhite
  }
}

const DANGER_THEME: Theme = {
  unselected: {
    backgroundColor: BRAND_COLOURS.brandRed100,
    borderColor: BRAND_COLOURS.brandRed500,
    circleColor: BRAND_COLOURS.brandRed500
  },
  selected: {
    backgroundColor: BRAND_COLOURS.brandRed500,
    borderColor: BRAND_COLOURS.brandRed500,
    circleColor: BRAND_COLOURS.brandWhite
  }
}

const SIZES = {
  small: 16,
  medium: 24,
  large: 32
}

const Switch = ({
  theme = 'default',
  variant = 'medium',
  ...switchProps
}: Props): JSX.Element => {
  const themes = {
    default: DEFAULT_THEME,
    success: SUCCESS_THEME,
    danger: DANGER_THEME
  }

  const themeStyles = themes[theme]

  const sizeStyles = useMemo(() => {
    const baseSize = SIZES[variant]
    return {
      height: baseSize,
      width: baseSize * 1.5,
      borderRadius: baseSize,
      unselected: {
        circleHeight: baseSize / 2,
        padding: baseSize / 4
      },
      selected: {
        circleHeight: (baseSize / 3) * 2,
        padding: baseSize / 6,
        translate: baseSize / 2
      }
    }
  }, [variant])

  const switchStyles: SxProps<MUITheme> = {
    height: sizeStyles.height,
    width: sizeStyles.width,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: `${sizeStyles.unselected.padding}px`
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      padding: `${sizeStyles.selected.padding}px`,
      transform: `translateX(${sizeStyles.selected.translate}px)`
    },
    '& .MuiSwitch-track': {
      boxSizing: 'border-box',
      borderRadius: sizeStyles.borderRadius,
      backgroundColor: themeStyles.unselected.backgroundColor as string,
      border: `1px solid ${themeStyles.unselected.borderColor}`,
      opacity: 1
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: themeStyles.selected.backgroundColor as string,
      border: `1px solid ${themeStyles.selected.borderColor}`,
      opacity: 1
    },
    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.5
    },
    '& .MuiSwitch-thumb': {
      height: sizeStyles.unselected.circleHeight,
      width: sizeStyles.unselected.circleHeight,
      backgroundColor: themeStyles.unselected.circleColor as string
    },
    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
      height: sizeStyles.selected.circleHeight,
      width: sizeStyles.selected.circleHeight,
      backgroundColor: themeStyles.selected.circleColor as string
    },
    '& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb': {
      opacity: 0.8
    }
  }

  return (
    <MUISwitch
      sx={switchStyles}
      {...switchProps}
      inputProps={
        {
          ...switchProps.inputProps,
          'data-theme': theme,
          'data-size': variant
        } as ExtendedInputProps
      }
    />
  )
}

export type { Props }

export { Switch }
